import { SearchQuery } from "shared/data/reducers/types/storage";
import { getFlightCombinationsTree } from "shared/gol-api/flightSearch";
import { getAverageFlightLength } from "shared/lib/functions";
import { isFalsy } from "shared/lib/isFalsy";
import { ComparatorResult } from "shared/types";
import FlightOffer from "shared/types/flightOffer";
import { HotelOfferDetail, RoomCategory } from "shared/types/hotels";

import filterAirports from "./filterFunctions/filterAirports";
import filterBaggageAndPrice from "./filterFunctions/filterBaggageAndPrice";
import filterFlightTimes from "./filterFunctions/filterFlightTimes";
import filterFlightWithInvalidCombinations from "./filterFunctions/filterFlightWithInvalidCombinations";
import filterFromTo from "./filterFunctions/filterFromTo";
import filterHotelName from "./filterFunctions/filterHotelName";
import filterHotelPrice from "./filterFunctions/filterHotelPrice";
import filterHotelStars from "./filterFunctions/filterHotelStars";
import filterMapViewPort from "./filterFunctions/filterMapViewPort";
import filterMaxTransfers from "./filterFunctions/filterMaxTransfers";
import filterMealOptions from "./filterFunctions/filterMealOptions";
import filterRefundableOnly from "./filterFunctions/filterRefundableOnly";
import filterRefundableOnlyHotel from "./filterFunctions/filterRefundableOnlyHotel";
import filterRoomPrice from "./filterFunctions/filterRoomPrice";
import filterTransfers from "./filterFunctions/filterTransfers";
import filterTransportCompanies from "./filterFunctions/filterTransportCompanies";

const cloneFlightOffersForFiltering = (
  flightOffers: FlightOffer[]
): FlightOffer[] => {
  return flightOffers.map((flightOffer) => ({
    ...flightOffer,
    FlightItinerary: {
      ...flightOffer.FlightItinerary,
      FlightStream: flightOffer.FlightItinerary.FlightStream.map(
        (flightStream) => [...flightStream]
      ),
    },
  }));
};

const ensureCurrentCombinations = (flightOffer: FlightOffer): FlightOffer => {
  if (flightOffer.CurrentCombinations) {
    return flightOffer;
  }

  return {
    ...flightOffer,
    CurrentCombinations: getFlightCombinationsTree(
      flightOffer.FlightCombinations
    ),
  };
};

const getTransferAirportsByFlightStream = (
  flightOffers: FlightOffer[]
): string[][] => {
  const transferAirportSets: Array<Set<string>> = [];

  flightOffers.forEach((flightOffer) => {
    flightOffer.FlightItinerary.FlightStream.forEach(
      (flightStream, flightStreamIndex) => {
        const transferAirportSet =
          transferAirportSets[flightStreamIndex] || new Set<string>();

        flightStream.forEach((flightOption) => {
          const flightSegments = flightOption.FlightSegments.FlightSegment;

          for (let i = 1; i < flightSegments.length; i++) {
            transferAirportSet.add(flightSegments[i].OriginAirport);
          }
        });

        transferAirportSets[flightStreamIndex] = transferAirportSet;
      }
    );
  });

  return transferAirportSets.map((transferAirportSet) =>
    Array.from(transferAirportSet)
  );
};

const filterOutFlightsWithoutBrands = (flightOffer: FlightOffer) => {
  return flightOffer.FlightCombinations.length !== 0;
};

const flightOffersComparator = (
  a: FlightOffer,
  b: FlightOffer,
  sortFlights: string
): ComparatorResult => {
  if (sortFlights === "PRICE") {
    const aFullPrice = Number(
      a.FlightCombinations[0].PricingDetail.FlightPricing.FlightPrice.FullPrice
    );
    const bFullPrice = Number(
      b.FlightCombinations[0].PricingDetail.FlightPricing.FlightPrice.FullPrice
    );
    if (aFullPrice > bFullPrice) {
      return 1;
    }
    if (aFullPrice < bFullPrice) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "DURATION_OPPORTUNITY_COST_PRICE") {
    const aFullPrice = Number(
      a.FlightCombinations[0].PricingDetail.FlightPricing.FlightPrice
        .DurationOpportunityCostPrice
    );
    const bFullPrice = Number(
      b.FlightCombinations[0].PricingDetail.FlightPricing.FlightPrice
        .DurationOpportunityCostPrice
    );
    if (aFullPrice > bFullPrice) {
      return 1;
    }
    if (aFullPrice < bFullPrice) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "NUM_TRANSFERS") {
    if (a.numTransfers > b.numTransfers) {
      return 1;
    }
    if (a.numTransfers < b.numTransfers) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "FLIGHT_LENGTH") {
    const averageFlightLengthA = getAverageFlightLength(a);
    const averageFlightLengthB = getAverageFlightLength(b);
    if (averageFlightLengthA > averageFlightLengthB) {
      return 1;
    }
    if (averageFlightLengthA < averageFlightLengthB) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "TIME_DEPARTURE") {
    if (
      +new Date(a.FlightItinerary.FlightStream[0][0].DepartureDateTime) >
      +new Date(b.FlightItinerary.FlightStream[0][0].DepartureDateTime)
    ) {
      return 1;
    }
    if (
      +new Date(a.FlightItinerary.FlightStream[0][0].DepartureDateTime) <
      +new Date(b.FlightItinerary.FlightStream[0][0].DepartureDateTime)
    ) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "PLACE_DEPARTURE") {
    if (
      a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[0]
        .OriginAirport >
      b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[0]
        .OriginAirport
    ) {
      return 1;
    }
    if (
      a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[0]
        .OriginAirport <
      b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[0]
        .OriginAirport
    ) {
      return -1;
    }
    return 0;
  }
  if (sortFlights === "PLACE_ARRIVAL") {
    if (
      a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[
        a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment
          .length - 1
      ].DestinationAirport >
      b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[
        b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment
          .length - 1
      ].DestinationAirport
    ) {
      return 1;
    }
    if (
      a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[
        a.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment
          .length - 1
      ].DestinationAirport <
      b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment[
        b.FlightItinerary.FlightStream[0][0].FlightSegments.FlightSegment
          .length - 1
      ].DestinationAirport
    ) {
      return -1;
    }
    return 0;
  }
  return 0;
};

const getFilteredFlightOffers = (
  flightOffers: FlightOffer[],
  searchQuery: SearchQuery
): FlightOffer[] => {
  const transferAirportsByFlightStream = getTransferAirportsByFlightStream(
    flightOffers
  );

  return cloneFlightOffersForFiltering(flightOffers)
    .map(filterBaggageAndPrice(searchQuery))
    .filter(filterOutFlightsWithoutBrands)
    .filter(filterMaxTransfers(searchQuery))
    .filter(filterFlightTimes(searchQuery))
    .filter(filterAirports(searchQuery, transferAirportsByFlightStream))
    .filter(filterTransportCompanies(searchQuery))
    .filter(filterFromTo(searchQuery))
    .filter(filterTransfers(searchQuery))
    .filter(filterFlightWithInvalidCombinations())
    .map(ensureCurrentCombinations);
};

const getFilteredHotelOffers = (
  hotelOffers: Record<string, HotelOfferDetail>,
  searchQuery: SearchQuery,
  mapViewport: {
    latMin: number;
    lngMin: number;
    latMax: number;
    lngMax: number;
  } | null = null
): HotelOfferDetail[] => {
  const hotelOfferArray = Object.values(hotelOffers);

  const filteredOffers = hotelOfferArray
    .filter(filterHotelStars(searchQuery as SearchQuery))
    .filter(filterHotelName(searchQuery as SearchQuery))
    .filter(filterMealOptions(searchQuery as SearchQuery))
    .filter(filterHotelPrice(searchQuery))
    .filter(filterMapViewPort(mapViewport))
    .filter(filterRefundableOnlyHotel(searchQuery as SearchQuery))
    .map((ho) => ho);

  return filteredOffers;
};

const getHotelStarCounts = (
  hotelOffers: HotelOfferDetail[]
): Record<number, number> => {
  return Object.values(hotelOffers).reduce((acc, offer) => {
    const stars = offer?.HotelDescription?.Stars;
    if (typeof stars === "string" && !isFalsy(stars)) {
      const starNum = Number(stars);
      if (!isNaN(starNum)) {
        acc[starNum] = (acc[starNum] || 0) + 1;
      }
    }
    return acc;
  }, {} as Record<number, number>);
};

const compareStars = (
  a: HotelOfferDetail,
  b: HotelOfferDetail,
  descending: boolean
): ComparatorResult => {
  const aStars = a.HotelDescription?.Stars;
  const bStars = b.HotelDescription?.Stars;

  if (!aStars && !bStars) return 0;
  if (!aStars) return 1;
  if (!bStars) return -1;

  const aNum = Number(aStars);
  const bNum = Number(bStars);
  const result = Math.sign(aNum - bNum) as ComparatorResult;
  return (descending ? result : -result) as ComparatorResult;
};

const hotelOffersComparator = (
  a: HotelOfferDetail,
  b: HotelOfferDetail,
  sortHotels: string
): ComparatorResult => {
  if (sortHotels === "MIN_PRICE_HOTEL") {
    const aFullPrice = Number(
      a.RoomCategoriesInfo.RoomCategoryPerNightPriceRange
        .PriceFromWithServiceFee
    );
    const bFullPrice = Number(
      b.RoomCategoriesInfo.RoomCategoryPerNightPriceRange
        .PriceFromWithServiceFee
    );
    if (aFullPrice > bFullPrice) {
      return 1;
    }
    if (aFullPrice < bFullPrice) {
      return -1;
    }
    return 0;
  }

  if (sortHotels === "MAX_PRICE_HOTEL") {
    const aFullPrice = Number(
      a.RoomCategoriesInfo.RoomCategoryPerNightPriceRange
        .PriceFromWithServiceFee
    );
    const bFullPrice = Number(
      b.RoomCategoriesInfo.RoomCategoryPerNightPriceRange
        .PriceFromWithServiceFee
    );
    if (aFullPrice < bFullPrice) {
      return 1;
    }
    if (aFullPrice > bFullPrice) {
      return -1;
    }
    return 0;
  }

  if (sortHotels === "STARS_HOTEL_HIGH_TO_LOW") {
    return compareStars(a, b, false);
  }

  if (sortHotels === "STARS_HOTEL_LOW_TO_HIGH") {
    return compareStars(a, b, true);
  }

  if (sortHotels === "REFUNDABLE_DATE") {
    const aToDateStr = a.RoomCategoriesInfo.CancellationDateRange?.ToDate;
    const bToDateStr = b.RoomCategoriesInfo.CancellationDateRange?.ToDate;

    const isANonRefundable = (() => {
      if (!aToDateStr) return false;
      const aToDate = new Date(aToDateStr);
      const today = new Date();
      aToDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Non-refundable if ToDate is today or earlier
      return aToDate.getTime() <= today.getTime();
    })();

    const isBNonRefundable = (() => {
      if (!bToDateStr) return false;
      const bToDate = new Date(bToDateStr);
      const today = new Date();
      bToDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Non-refundable if ToDate is today or earlier
      return bToDate.getTime() <= today.getTime();
    })();

    if (isANonRefundable && !isBNonRefundable) return 1;
    if (!isANonRefundable && isBNonRefundable) return -1;
    if (isANonRefundable && isBNonRefundable) return 0;

    const aDate = a.RoomCategoriesInfo.CancellationDateRange?.ToDate;
    const bDate = b.RoomCategoriesInfo.CancellationDateRange?.ToDate;

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;

    const aNum = new Date(aDate).getTime();
    const bNum = new Date(bDate).getTime();
    const result = Math.sign(bNum - aNum) as ComparatorResult;
    return result;
  }

  return 0;
};

const getHotelNameOptions = (
  hotelOffers: HotelOfferDetail[]
): { label: string; value: string }[] => {
  const hotelNames = new Set<string>();
  Object.values(hotelOffers).forEach((offer) => {
    const name = offer?.HotelDescription?.Name?.$t;
    if (name) {
      hotelNames.add(name);
    }
  });
  return Array.from(hotelNames)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      label: name,
      value: name,
    }));
};

export {
  flightOffersComparator,
  getFilteredFlightOffers,
  getFilteredHotelOffers,
  getHotelStarCounts,
  hotelOffersComparator,
  getFilteredHotelRooms,
  getHotelRoomPrices,
  getHotelPrices,
  getHotelNameOptions,
  getMealOptionsAndCounts,
};

const getFilteredHotelRooms = (
  hotelRooms: any[],
  searchQuery: SearchQuery
): any[] => {
  if (!hotelRooms) {
    return null;
  }

  const filteredRooms = hotelRooms
    .filter(filterRoomPrice(searchQuery))
    .filter(filterRefundableOnly(searchQuery))
    .filter(filterMealOptions(searchQuery as SearchQuery))
    .map((hr) => hr);

  return filteredRooms;
};

const getPrice = <T>(
  items: T[],
  getPriceValue: (item: T) => number
): { min: number; max: number } => {
  if (!items || items.length === 0) {
    return { min: 0, max: 0 };
  }

  const prices = items.map(getPriceValue);
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
};

const getHotelRoomPrices = (
  hotelRooms: any[]
): { min: number; max: number } => {
  return getPrice(hotelRooms, (room) => Number(room.PriceWithServiceFee));
};

const getHotelPrices = (
  hotelOffers: HotelOfferDetail[]
): { min: number; max: number } => {
  return getPrice(hotelOffers, (hotel) =>
    Number(
      hotel.RoomCategoriesInfo
        ?.AlternativeCurrencyRoomCategoryPerNightPriceRange
        ?.PriceFromWithServiceFee ||
        hotel.RoomCategoriesInfo.RoomCategoryPerNightPriceRange
          .PriceFromWithServiceFee
    )
  );
};

const getMealOptionsAndCounts = (
  allOffers: (HotelOfferDetail | RoomCategory)[]
): {
  mealOptions: { label: string; value: string }[];
  mealOptionCounts: Record<string, number>;
} => {
  const mealOptionsMap = new Map<string, string>();
  const mealOptionCounts = {};

  if (!allOffers || allOffers.length === 0) {
    return { mealOptions: [], mealOptionCounts: {} };
  }

  Object.values(allOffers).forEach((offer) => {
    const processMealOptions = (meal) => {
      const code = meal.Basis.Code;
      const name = meal.Basis.$t;
      if (code && name && !mealOptionsMap.has(code)) {
        mealOptionsMap.set(code, name);
      }
    };

    if ("RoomCategoriesInfo" in offer) {
      const meals = offer?.RoomCategoriesInfo?.MealsOptions?.Meals;
      if (meals) {
        meals.forEach(processMealOptions);
      }
    } else if ("Meals" in offer) {
      const meal = offer?.Meals;
      if (meal) {
        processMealOptions(meal);
      }
    }
  });

  Object.values(allOffers).forEach((offer) => {
    const processMealCounts = (meal) => {
      const code = meal.Basis.Code;
      if (code) {
        mealOptionCounts[code] = (mealOptionCounts[code] || 0) + 1;
      }
    };

    if ("RoomCategoriesInfo" in offer) {
      const meals = offer?.RoomCategoriesInfo?.MealsOptions?.Meals;
      if (meals) {
        meals.forEach(processMealCounts);
      }
    } else if ("Meals" in offer) {
      const meal = offer?.Meals;
      if (meal) {
        processMealCounts(meal);
      }
    }
  });

  const mealOptions = Array.from(mealOptionsMap.entries())
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([code, name]) => ({
      label: name,
      value: code,
    }));

  return { mealOptions, mealOptionCounts };
};
